import {
  isBkashPaymentMethod,
  isChequePaymentMethod,
  requiresDepositorBankAccount,
} from './paymentProofFormState'
import { formatDate } from './dateFormat'

export {
  isBkashPaymentMethod,
  isChequePaymentMethod,
  isCashDepositPaymentMethod,
  requiresDepositorBankAccount,
} from './paymentProofFormState'

export function formatDash(value) {
  if (value == null || value === '') return '—'
  return String(value)
}

export function formatTransferType(method) {
  const upper = String(method || '').toUpperCase()
  if (!upper) return '—'
  if (upper === 'BEFTN') return 'BEFTN'
  if (upper === 'NPSB') return 'NPSB'
  if (upper === 'RTGS') return 'RTGS'
  if (upper === 'CASH_DEPOSIT') return 'Cash deposit'
  if (upper === 'BKASH') return 'bKash'
  if (upper === 'CHEQUE') return 'Cheque'
  if (upper === 'EXISTING_BALANCE') return 'Existing balance'
  return upper.replace(/_/g, ' ')
}

/** @deprecated Prefer isBkashPaymentMethod */
export function isMobileWalletMethod(method) {
  return isBkashPaymentMethod(method)
}

/**
 * Read-only detail rows for admin order detail / proof previews.
 * Omits depositor bank fields for cash deposit, cheque, and bKash.
 */
export function getAdminPaymentProofDetailFields(proof) {
  if (!proof) return []
  const method = proof.payment_method
  const rows = []

  if (requiresDepositorBankAccount(method)) {
    rows.push(
      { key: 'bank', label: 'Bank name', value: proof.bank_name },
      { key: 'branch', label: 'Branch', value: proof.branch_name },
      {
        key: 'routing',
        label: 'Routing number',
        value: proof.routing_number,
        copyable: true,
      },
      {
        key: 'account',
        label: 'Account number',
        value: proof.account_number,
        copyable: true,
      },
    )
  }

  if (isBkashPaymentMethod(method)) {
    rows.push(
      {
        key: 'bkashPhone',
        label: 'Phone No',
        value: proof.bkash_phone || proof.account_number,
        copyable: true,
      },
      {
        key: 'bkashTxn',
        label: 'Transaction ID',
        value: proof.bkash_transaction_id,
        copyable: true,
      },
    )
  }

  if (isChequePaymentMethod(method)) {
    rows.push({
      key: 'cheque',
      label: 'Cheque No',
      value: proof.cheque_number,
      copyable: true,
    })
    rows.push({
      key: 'chequeDate',
      label: 'Cheque date',
      value: proof.cheque_date,
    })
  }

  return rows
}

export function formatAdminPaymentProofSubtitle(proof) {
  if (!proof) return ''
  const parts = [formatTransferType(proof.payment_method)]
  const amount = Number(proof.requested_amount ?? 0)
  if (Number.isFinite(amount)) {
    parts.push(`৳${amount.toFixed(2)}`)
  }
  if (requiresDepositorBankAccount(proof.payment_method)) {
    const acct = String(proof.account_number || '').trim()
    if (acct) parts.push(acct)
  }
  if (isBkashPaymentMethod(proof.payment_method)) {
    const phone = String(proof.bkash_phone || proof.account_number || '').trim()
    const txn = String(proof.bkash_transaction_id || '').trim()
    if (phone) parts.push(phone)
    if (txn) parts.push(txn)
  }
  if (isChequePaymentMethod(proof.payment_method)) {
    const chq = String(proof.cheque_number || '').trim()
    const chqDateLabel = formatDate(proof.cheque_date, '')
    const hasChqDate = chqDateLabel && chqDateLabel !== '—'
    if (chq && hasChqDate) {
      parts.push(`Chq ${chq} · ${chqDateLabel}`)
    } else if (chq) {
      parts.push(`Chq ${chq}`)
    } else if (hasChqDate) {
      parts.push(`Chq date ${chqDateLabel}`)
    }
  }
  return parts.filter((p) => p && p !== '—').join(' · ')
}

export function formatShwapnoReceivingAccountOption(account) {
  if (!account) return '—'
  if (account.label) return String(account.label)
  if (account.type === 'mfs_account') {
    const parts = [
      account.mfs_name || account.bank_name,
      account.contact_number || account.account_number || account.phone_number,
    ].filter(Boolean)
    return parts.length ? parts.join(' · ') : '—'
  }
  const parts = [
    account.bank_name,
    account.account_number,
    account.branch_name,
  ].filter(Boolean)
  return parts.length ? parts.join(' · ') : '—'
}

export function getShwapnoReceivingAccountLabel(proof, bankAccountBySettingsId) {
  const backendLabel = proof?.shwapno_receiving_account_label
  if (backendLabel) return backendLabel
  const settingsId = proof?.shwapno_payment_settings_id
  if (settingsId == null) return '—'
  const account = bankAccountBySettingsId?.[String(settingsId)]
  return formatShwapnoReceivingAccountOption(account)
}

export function isPaymentProofFilePdf(fileUrl) {
  const url = String(fileUrl || '').toLowerCase()
  return url.endsWith('.pdf') || url.includes('application/pdf')
}

function paymentUploadFileUrl(item) {
  if (typeof item === 'string') return item.trim()
  if (item && typeof item === 'object') {
    return String(item.url || item.fileUrl || '').trim()
  }
  return ''
}

function paymentUploadProofId(item) {
  if (!item || typeof item !== 'object') return null
  const raw = item.proof_id ?? item.proofId
  const id = Number(raw)
  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * Files stored on order.meta.payment_upload that have no payment_proofs row.
 * New uploads also write into payment_upload, so skip URLs and proof ids
 * already present on structured proofs.
 */
export function legacyPaymentProofFiles(order, structuredProofs = []) {
  const raw = order?.meta?.payment_upload
  if (!Array.isArray(raw)) return []

  const structuredUrls = new Set(
    (structuredProofs || [])
      .map((proof) => String(proof?.file_url || '').trim())
      .filter(Boolean),
  )
  const structuredIds = new Set(
    (structuredProofs || [])
      .map((proof) => Number(proof?.id))
      .filter((id) => Number.isInteger(id) && id > 0),
  )

  const seen = new Set()
  const files = []
  raw.forEach((item, index) => {
    const fileUrl = paymentUploadFileUrl(item)
    if (!fileUrl || seen.has(fileUrl)) return
    const proofId = paymentUploadProofId(item)
    if (proofId && structuredIds.has(proofId)) return
    if (structuredUrls.has(fileUrl)) return
    seen.add(fileUrl)
    files.push({
      id: `legacy-${index}`,
      file_url: fileUrl,
      legacy: true,
      payment_at: order?.payment_proof_datetime || null,
    })
  })
  return files
}

/**
 * Older orders were marked Paid when the uploaded file was accepted, without a
 * payment_proofs amount. The summary otherwise counts only the new records, so
 * a paid order can show ৳0 received. Treat that earlier file as payment of the
 * full receivable.
 */
export function paymentSummaryWithLegacyPaidOrder(order, summary, legacyFiles) {
  if (!summary || !legacyFiles?.length) return summary
  if (String(order?.payment_status || '').toUpperCase() !== 'PAID') return summary

  const payable = Number(summary.payable ?? 0)
  const received = Number(summary.received ?? 0)
  if (!Number.isFinite(payable) || payable <= 0) return summary
  if (!Number.isFinite(received) || received >= payable) return summary

  const requested = Number(summary.requested ?? 0)
  return {
    ...summary,
    received: payable,
    remaining: 0,
    requested: Math.max(Number.isFinite(requested) ? requested : 0, payable),
    available_to_submit: 0,
  }
}

export function isPaymentProofFileImage(fileUrl) {
  const url = String(fileUrl || '').toLowerCase()
  if (isPaymentProofFilePdf(url)) return false
  return (
    /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(url) || url.includes('image/')
  )
}

export async function copyTextToClipboard(text) {
  const value = String(text || '').trim()
  if (!value) return false
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}

function paymentProcessingModeToMethod(mode) {
  const raw = String(mode || '').trim()
  if (!raw) return ''
  if (!raw.includes(' ') && !raw.includes('_')) return raw.toUpperCase()
  return raw.toUpperCase().replace(/\s+/g, '_')
}

export function formatShwapnoReceivingFromProcessingSummary(proof) {
  if (!proof) return null
  const parts = [proof.deposited_bank_shwapno, proof.shwapno_account_or_mobile]
    .map((part) => (part != null ? String(part).trim() : ''))
    .filter(Boolean)
  return parts.length ? parts.join(' · ') : null
}

/** Deposit-level status label for payment processing preview (matches partner shop copy). */
export function getPaymentProofItemStatusLabel(verificationStatus) {
  const upper = String(verificationStatus || '').toUpperCase()
  if (upper === 'VERIFIED') return 'Verified'
  if (upper === 'REJECTED') return 'Rejected'
  return 'Awaiting verification'
}

export function getPaymentProcessingPreviewStatusLabel(row) {
  const statusUpper = String(row?.status || '').toUpperCase()
  if (statusUpper === 'VERIFIED') return 'Verified'
  if (statusUpper === 'REJECTED') return 'Rejected'
  if (row?.edited_after_rejection) return 'Edited — awaiting verification'
  return 'Awaiting verification'
}

export function inferPaymentProofFileLabel(fileUrl, proofId, index) {
  const fallback = `Proof ${proofId != null ? `#${proofId}` : index + 1}`
  const raw = String(fileUrl || '').split('?')[0]
  const segment = raw.split('/').pop() || ''
  if (segment && segment.includes('.')) {
    try {
      return decodeURIComponent(segment)
    } catch {
      return segment
    }
  }
  return fallback
}

/**
 * Map a payment-processing list row into read-only preview proof objects for admin UI.
 */
export function mapPaymentProcessingRowToPreviewProofs(row) {
  if (!row) return []

  const mapSummary = (summary, index) => {
    const method = summary.payment_method
      ? String(summary.payment_method).toUpperCase()
      : paymentProcessingModeToMethod(summary.mode_of_deposit)
    const txnOrChq = summary.transaction_or_cheque_id
    return {
      id: summary.proof_id ?? index,
      file_url: summary.file_url ?? null,
      file_label: inferPaymentProofFileLabel(
        summary.file_url,
        summary.proof_id,
        index,
      ),
      payment_method: method,
      requested_amount:
        summary.requested_amount != null
          ? summary.requested_amount
          : row.amount,
      payment_at: summary.payment_at ?? row.transaction_at ?? null,
      bank_name: summary.bank_name ?? null,
      branch_name: summary.branch_name ?? null,
      routing_number: summary.routing_number ?? null,
      account_number:
        summary.account_number || summary.depositor_account_no || null,
      bkash_phone: summary.bkash_phone ?? null,
      bkash_transaction_id:
        summary.bkash_transaction_id ||
        (isBkashPaymentMethod(method) ? txnOrChq : null),
      cheque_number:
        summary.cheque_number ||
        (isChequePaymentMethod(method) ? txnOrChq : null),
      cheque_date: summary.cheque_date ?? row.cheque_date ?? null,
      shwapno_receiving_account_label:
        formatShwapnoReceivingFromProcessingSummary(summary),
      verification_status: summary.verification_status ?? null,
      received_amount: summary.received_amount ?? null,
    }
  }

  const proofs = Array.isArray(row.proofs) ? row.proofs : []
  if (proofs.length > 0) {
    return proofs.map(mapSummary)
  }

  const slipUrl = row.deposit_slip_url
  if (!slipUrl) return []

  const method = paymentProcessingModeToMethod(row.mode_of_deposit)
  return [
    mapSummary(
      {
        proof_id: row.proof_id,
        file_url: slipUrl,
        mode_of_deposit: row.mode_of_deposit,
        payment_method: method,
        requested_amount: row.amount,
        payment_at: row.transaction_at,
        depositor_account_no: row.depositor_account_no,
        transaction_or_cheque_id: row.transaction_or_cheque_id,
        cheque_date: row.cheque_date,
        deposited_bank_shwapno: row.deposited_bank_shwapno,
        shwapno_account_or_mobile: row.shwapno_account_or_mobile,
        bank_name: null,
        branch_name: null,
      },
      0,
    ),
  ]
}
